import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { User } from 'firebase/auth';
import { Capacitor } from '@capacitor/core';
import {

  Box,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  useTheme,
  Avatar,
  Button,
  Skeleton,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Mic, Clear, Star } from '@mui/icons-material';
import { MagnifyingGlassIcon, ArrowLeftIcon, UserIcon, StarIcon, FilmSlateIcon, ListIcon, HeartIcon } from '@phosphor-icons/react';
import Masonry from 'react-masonry-css';
import '../../styles/masonry.css';

import { TMDbMovie, tmdbService } from '../../services/tmdbApi';
import { SearchService } from '../../services/searchService';
import { recentActivityService } from '../../services/recentActivityService';
import { favoritePeopleService } from '../../services/favoritePeopleService';
import { searchAnalytics } from '../../utils/searchAnalytics';
import { stashPersonDetailNavPreviewFromPerson } from '../../utils/personDetailNavPreview';
import { stashMovieDetailNavPreviewFromMovie } from '../../utils/movieDetailNavPreview';
import { analyzeSearchQuery } from '../../utils/searchQueryAnalyzer';
import { enhancedSearchAnalytics } from '../../utils/enhancedSearchAnalytics';

import { useUserWatchlistData } from '../../contexts/UserWatchlistDataContext';
import { useUserFollow } from '../../contexts/UserFollowContext';
import { useUserProfileCache } from '../../contexts/UserProfileCacheContext';

import CustomTabs from '../widgets/CustomTabs';
import StandardMovieCard from '../widgets/StandardMovieCard';
import WatchlistWidget from '../widgets/WatchlistWidget';
import UserListingCard from '../widgets/UserListingCard';
import EmptyState from '../widgets/EmptyState';
import InfiniteScrollContainer from '../widgets/InfiniteScrollContainer';
import ImageShimmer from '../widgets/ImageShimmer';
import { useToast } from '../utils/ToastProvider';
import { UserProfile as UserProfileType, dataService, movieService } from '@watchlist/shared';
import { useUserMovieData } from '../../contexts/UserMovieDataContext';
import { getStandardizedMovieId } from '../../utils/movieUtils';
import WatchlistCommunityRatingIcon from '../widgets/WatchlistCommunityRatingIcon';
import ResponsiveContainer from '../layouts/ResponsiveContainer';
import PullToRefresh from '../common/PullToRefresh';
import { useScrollContainer, getScrollTop } from '../layout/ScrollContainer';
import { edgeToEdgeSx } from '../../utils/scrollStyles';
import { font } from '../../utils/fontHelper';
import {
  PERF_SEARCH_TO_MOVIE_START_KEY,
  startTrace,
  stopTrace,
  TraceNames,
} from '../../utils/performanceMonitoring';
import { useMovieQuickView } from '../../contexts/MovieQuickViewContext';
import { useSwipeTab } from '../../hooks/useSwipeTab';

const TAB_MOVIES = 1;
const TAB_WATCHLISTS = 2;
const TAB_USERS = 3;
const TAB_PEOPLE = 4;

// Module-level search cache — survives component remounts/navigation.
// useRef would reset every time SearchPage unmounts (e.g. on Capacitor navigation),
// losing cached results and forcing full refetches on return.
const SEARCH_CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const MODULE_SEARCH_CACHE = new Map<string, {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  results: any;
  timestamp: number;
  extraMovies?: TMDbMovie[];
}>();

/** Firestore aggregation row id for admin Performance tab */
function getSearchResultsPerfPageName(tabToSearch: number): string {
  switch (tabToSearch) {
    case TAB_MOVIES:
      return 'search_movies_results';
    case TAB_WATCHLISTS:
      return 'search_watchlists_results';
    case TAB_USERS:
      return 'search_users_results';
    case TAB_PEOPLE:
      return 'search_people_results';
    default:
      return 'search_results';
  }
}

async function reportSearchResultsPerf(
  pageName: string,
  startedAt: number,
  cacheHit: boolean
): Promise<void> {
  try {
    const { reportPageLoadToFirestore } = await import('../../services/performanceAggregationService');
    await reportPageLoadToFirestore({
      pageName,
      loadTimeMs: Math.max(0, Math.round(performance.now() - startedAt)),
      cacheHit,
      timestamp: new Date().toISOString(),
    });
  } catch {
    /* ignore */
  }
}

interface SearchPageProps {
  user?: User;
  onMovieSelect: (movieId: string) => void;
  onWatchlistSelect: (watchlistId: string, tabIndex?: number, userId?: string) => void;
  onUserProfileSelect: (userId: string) => void;
  onPersonSelect: (personId: string) => void;
  onBack: () => void;
  initialQuery?: string;
  initialTab?: number;
  onSearchStateChange?: (query: string, tab: number) => void;
}

const SearchPage: React.FC<SearchPageProps> = ({
  user,
  onMovieSelect,
  onWatchlistSelect,
  onUserProfileSelect,
  onPersonSelect,
  onBack,
  initialQuery = '',
  initialTab,
  onSearchStateChange,
}) => {
  const theme = useTheme();
  const skBg =
    theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)';
  const inputRef = useRef<HTMLInputElement>(null);
  const { showError, showWarning } = useToast();

  // Search state
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  // Two-stage Search tab: default is Explore (no focus / no tabs); Active mode
  // surfaces the existing search page with tabs + recent + results.
  const [searchActive, setSearchActive] = useState<boolean>(initialQuery.trim().length > 0);
  const [searchResults, setSearchResults] = useState<{
    movies: TMDbMovie[];
    watchlists: any[];
    people: any[];
    profiles: UserProfileType[];
    movieMatchInfo: Map<string, { matchedFields: string[]; relevanceScore: number }>; // Map movie ID to match info
    pagination: {
      movies: { current: number; total: number; hasMore: boolean };
      watchlists: { current: number; total: number; hasMore: boolean };
      people: { current: number; total: number; hasMore: boolean };
      profiles: { current: number; total: number; hasMore: boolean };
    };
    breakdown: {
      movies: { total: number; returned: number };
      watchlists: { total: number; returned: number };
      profiles: { total: number; returned: number };
      people: { total: number; returned: number };
    };
  }>({
    movies: [],
    watchlists: [],
    people: [],
    profiles: [],
    movieMatchInfo: new Map(),
    pagination: {
      movies: { current: 1, total: 0, hasMore: false },
      watchlists: { current: 1, total: 0, hasMore: false },
      people: { current: 1, total: 0, hasMore: false },
      profiles: { current: 1, total: 0, hasMore: false }
    },
    breakdown: {
      movies: { total: 0, returned: 0 },
      watchlists: { total: 0, returned: 0 },
      profiles: { total: 0, returned: 0 },
      people: { total: 0, returned: 0 }
    }
  });
  const hasInitialQuery = initialQuery.trim().length >= 2;
  const [searchLoading, setSearchLoading] = useState(hasInitialQuery);
  const [searchUpdating, setSearchUpdating] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasSearched, setHasSearched] = useState(hasInitialQuery);
  const [activeSearchTab, setActiveSearchTab] = useState(initialTab ?? TAB_MOVIES);
  const [currentSearchQuery, setCurrentSearchQuery] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  // Enhanced search — only via header icon (shown when query has 2+ words); never auto

  // Recent items state
  const [recentMovies, setRecentMovies] = useState<any[]>([]);
  const [recentWatchlists, setRecentWatchlists] = useState<any[]>([]);
  const [recentUsers, setRecentUsers] = useState<any[]>([]);
  const [recentPeople, setRecentPeople] = useState<any[]>([]);
  const [loadingRecent, setLoadingRecent] = useState(false);
  const [recentMoviesCommunityRatings, setRecentMoviesCommunityRatings] = useState<Map<string, number>>(
    () => new Map()
  );

  // Voice search state (web only — mic hidden on Capacitor iOS/Android)
  const [isListening, setIsListening] = useState(false);
  const [speechRecognition, setSpeechRecognition] = useState<any>(null);
  const showVoiceSearch = !Capacitor.isNativePlatform();

  // Request tracking for race condition prevention
  const abortControllerRef = useRef<AbortController | null>(null);
  const searchRequestIdRef = useRef<number>(0);
  // Ref that always mirrors the latest searchResults — lets the performSearch useCallback
  // read current results without needing searchResults in its dep array (which would
  // cause a new function reference on every result update and re-trigger the debounce).
  const searchResultsRef = useRef(searchResults);
  searchResultsRef.current = searchResults;
  // In-flight dedupe: collapses double-fires (rapid keystrokes, tab+keystroke races)
  // for the same (query, tab, mode) into a single TMDB hit.
  const inFlightSearchRef = useRef<Set<string>>(new Set());
  // Guards against the debounce effect re-firing 450ms after a tab change that already
  // called performSearch directly (handleSearchTabChange fires immediately; the effect
  // fires again because activeSearchTab is in its dep array).
  const lastImmediateSearchRef = useRef('');
  // Guards against the debounce effect re-firing on return from a movie detail
  // (or any non-tab navigation) when performSearch's identity changed but the
  // user's actual query+tab didn't. Without this dedup, the cache-restore on
  // page=1 wipes the accumulated load-more pages, shrinks the list, and the
  // tab's preserved scrollTop ends up past the new content end.
  const lastDebouncedSearchKeyRef = useRef('');

  // Search result cache helpers — backed by MODULE_SEARCH_CACHE (module scope, survives navigation)
  const getCacheKey = useCallback((query: string, tab: number, useEnhanced: boolean) => {
    return `${query.trim().toLowerCase()}_tab${tab}_${useEnhanced ? 'enh' : 'basic'}`;
  }, []);

  const getCachedResults = useCallback((query: string, tab: number, useEnhanced: boolean) => {
    const cacheKey = getCacheKey(query, tab, useEnhanced);
    const cached = MODULE_SEARCH_CACHE.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < SEARCH_CACHE_TTL) {
      console.log('🎯 SearchPage: Cache hit for:', cacheKey);
      return cached;
    }
    return null;
  }, [getCacheKey]);

  const cacheResults = useCallback((query: string, tab: number, results: typeof searchResults, extraMovies?: TMDbMovie[], useEnhanced?: boolean) => {
    const cacheKey = getCacheKey(query, tab, Boolean(useEnhanced));
    MODULE_SEARCH_CACHE.set(cacheKey, {
      results,
      timestamp: Date.now(),
      extraMovies
    });
    console.log('💾 SearchPage: Cached results for:', cacheKey, useEnhanced ? '(enhanced)' : '(basic)');

    // LRU cleanup — keep max 20 entries
    if (MODULE_SEARCH_CACHE.size > 20) {
      const entries = Array.from(MODULE_SEARCH_CACHE.entries());
      entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
      entries.slice(0, 5).forEach(([key]) => MODULE_SEARCH_CACHE.delete(key));
    }
  }, [getCacheKey]);

  // Context hooks
  const { searchWatchlists, allWatchlists } = useUserWatchlistData();
  const { isUserFollowed, followUser, unfollowUser } = useUserFollow();
  const { getUserDisplayName, getUserUsername, getUserAvatar } = useUserProfileCache();
  const { getUserMovieData } = useUserMovieData();

  const placeholderText = useMemo(() => 'Search...', []);

  const getWatchlistDisplayData = useCallback((recentWatchlist: any) => {
    if (!recentWatchlist) return null;

    const contextWatchlist = allWatchlists.find(w => w.id === recentWatchlist.id) || null;
    const storedData = (recentWatchlist.data || {}) as any;

    const id = contextWatchlist?.id || recentWatchlist.id;
    const nameCandidates = [
      storedData.name,
      storedData.title,
      storedData.watchlistName,
      contextWatchlist?.name
    ].filter((value): value is string => typeof value === 'string' && value.trim().length > 0);
    const name = nameCandidates[0] || 'Watchlist';

    const createdBy = storedData.createdBy || storedData.creatorUid || contextWatchlist?.createdBy || '';
    const description = storedData.description ?? storedData.summary ?? contextWatchlist?.description ?? '';

    const movieDetails =
      (Array.isArray(contextWatchlist?.movieDetails) && contextWatchlist!.movieDetails.length > 0)
        ? contextWatchlist!.movieDetails
        : (Array.isArray(storedData.movieDetails) && storedData.movieDetails.length > 0)
          ? storedData.movieDetails
          : (Array.isArray(storedData.movies) ? storedData.movies : []);

    const collaborators = storedData.collaborators || contextWatchlist?.collaborators || [];
    const followerCount = storedData.followerCount || storedData.likesCount || contextWatchlist?.followerCount || 0;
    const visibility = storedData.visibility || (contextWatchlist?.isPrivate ? 'private' : 'public');

    const createdByName = createdBy
      ? getUserDisplayName(createdBy, contextWatchlist?.createdByName || storedData.createdByName || 'Unknown User')
      : (contextWatchlist?.createdByName || storedData.createdByName || 'Anonymous User');
    const createdByAvatar = createdBy
      ? getUserAvatar(createdBy)
      : (contextWatchlist?.createdByAvatar || storedData.createdByAvatar);

    return {
      ...(contextWatchlist || {}),
      id,
      name,
      description,
      createdBy,
      createdByName,
      createdByAvatar,
      isPrivate: visibility === 'private',
      movieDetails,
      collaborators,
      followerCount,
      updatedAt: storedData.updatedAt || contextWatchlist?.updatedAt,
      createdAt: storedData.createdAt || contextWatchlist?.createdAt
    };
  }, [allWatchlists, getUserAvatar, getUserDisplayName]);


  // Auto-focus only when in Active search mode (not the Explore default).
  useEffect(() => {
    if (searchActive && inputRef.current) {
      inputRef.current.focus();
      setIsSearchFocused(true);
    }
  }, [searchActive]);

  // Warm up the Cloud Function early so cold-start latency is absorbed by the
  // time the user finishes typing.
  useEffect(() => {
    SearchService.warmUp();
  }, []);

  // External signal — re-tapping the Search nav while already on /search.
  useEffect(() => {
    const onActivate = () => setSearchActive(true);
    window.addEventListener('search:activate', onActivate);
    return () => window.removeEventListener('search:activate', onActivate);
  }, []);

  // External signal — "Add movie" FAB menu item: navigate to search with input focused.
  useEffect(() => {
    const onFocus = () => {
      setSearchActive(true);
      setTimeout(() => inputRef.current?.focus(), 50);
    };
    window.addEventListener('search:focus', onFocus);
    return () => window.removeEventListener('search:focus', onFocus);
  }, []);

  // Handle scroll effect for sticky header
  const scrollSource = useScrollContainer();
  const { openMovieQuickView } = useMovieQuickView();
  useEffect(() => {
    const target: EventTarget = scrollSource ?? window;
    const handleScroll = () => setIsScrolled(getScrollTop(scrollSource) > 10);
    target.addEventListener('scroll', handleScroll);
    return () => target.removeEventListener('scroll', handleScroll);
  }, [scrollSource]);

  // Active background cleanup for duplicate movie records in Firebase
  useEffect(() => {
    if (!user?.uid || recentMovies.length < 2) return;

    const performCleanup = async () => {
      const seenCanonicalIds = new Map<string, string>(); // canonicalId -> firstMovieId
      const redundantIds: string[] = [];

      recentMovies.forEach(recentMovie => {
        if (!recentMovie?.data) return;
        const movie = recentMovie.data as TMDbMovie;
        const movieId = recentMovie.id;

        // Determine canonical ID for this movie
        let canonicalId = movie.id ? `tmdb_${movie.id}` : movieId;

        if (seenCanonicalIds.has(canonicalId)) {
          const firstId = seenCanonicalIds.get(canonicalId);
          // If this is a different Firebase document ID but the same movie content
          if (firstId !== movieId) {
            redundantIds.push(movieId);
          }
        } else {
          seenCanonicalIds.set(canonicalId, movieId);
        }

        // Also check if the current movieId is NOT the canonical one but we have metadata to make it so
        if (movie.id && movieId !== `tmdb_${movie.id}`) {
          // This entry should be "migrated" by tracking it again with the correct ID
          // and deleting the old one. trackMovie already handles the merge if we call it.
          console.log(`SearchPage: Found non-canonical movie ID ${movieId}, will migrate to tmdb_${movie.id}`);
          redundantIds.push(movieId);
          recentActivityService.trackMovie(`tmdb_${movie.id}`, movie, user.uid);
        }
      });

      if (redundantIds.length > 0) {
        console.log(`SearchPage: Cleaning up ${redundantIds.length} redundant movie records from Firebase:`, redundantIds);
        // Process deletions sequentially to avoid race conditions
        for (const id of redundantIds) {
          await recentActivityService.deleteRecentItem(user.uid, 'movies', id);
        }
      }
    };

    // Run cleanup with a slight delay to not interfere with initial render
    const timer = setTimeout(performCleanup, 2000);
    return () => clearTimeout(timer);
  }, [user?.uid, recentMovies.length]); // Only run when the list length changes or on user change

  // Clear all recent activity
  const handleClearRecentActivity = useCallback(async () => {
    if (!user) return;

    try {
      await recentActivityService.clearAll(user.uid);
      setRecentMovies([]);
      setRecentWatchlists([]);
      setRecentUsers([]);
      setRecentPeople([]);
    } catch (error) {
      console.error('Error clearing recent activity:', error);
      showError('Failed to clear recent activity');
    }
  }, [user, showError]);

  // Load recent items from Firebase
  useEffect(() => {
    const loadRecentItems = async () => {
      if (!user?.uid) return;

      setLoadingRecent(true);
      try {
        const [movies, watchlists, users, people] = await Promise.all([
          recentActivityService.getRecentMovies(user.uid, 12),
          recentActivityService.getRecentWatchlists(user.uid, 4),
          recentActivityService.getRecentUsers(user.uid, 4),
          recentActivityService.getRecentPeople(user.uid, 4),
        ]);

        // Unified normalization helper for consistency
        const normalizeId = (id: string): string => {
          if (!id) return '';
          const idStr = id.toString();
          const tmdbMatch = idStr.match(/tmdb_(\d+)/i);
          if (tmdbMatch) return tmdbMatch[1];
          const numericMatch = idStr.match(/(\d+)$/);
          if (numericMatch) return numericMatch[1];
          return idStr.toLowerCase().trim();
        };

        // Filter out broken movies and deduplicate using ALL available IDs (TMDB & IMDb)
        const seenIds = new Set<string>();
        const filteredMovies = movies.filter(recentMovie => {
          if (!recentMovie?.data) return false;
          const movie = recentMovie.data as TMDbMovie;
          const movieId = recentMovie.id;

          const hasTitle = movie.Title || movie.title;
          const poster = movie.poster_path || movie.Poster;
          const hasPoster = poster && poster !== 'N/A';
          const isPlaceholderTitle = hasTitle === 'Movie' || (hasTitle && hasTitle.length > 20 && (hasTitle.includes('_') || hasTitle.includes('-')));

          if (!hasTitle || isPlaceholderTitle || !hasPoster) {
            console.warn('SearchPage: Filtering broken movie at load time:', { id: movieId, title: hasTitle });
            return false;
          }

          // Check for ANY ID collision (TMDB primary, TMDB numeric from metadata, or IMDb ID)
          const idsToCheck = new Set<string>();
          idsToCheck.add(normalizeId(movieId));
          if (movie.id) idsToCheck.add(normalizeId(`tmdb_${movie.id}`));
          const imdbId = movie.imdb_id || movie.imdbID;
          if (imdbId) idsToCheck.add(imdbId.toLowerCase().trim());

          let isDuplicate = false;
          for (const id of idsToCheck) {
            if (seenIds.has(id)) {
              isDuplicate = true;
              break;
            }
          }

          if (isDuplicate) return false;

          // Track all IDs for this movie
          idsToCheck.forEach(id => seenIds.add(id));
          return true;
        });

        // Fetch community ratings in the same async flow so all state is set together,
        // avoiding a second render pass where star ratings pop in after the cards.
        const movieIds = filteredMovies
          .map(r => { try { return getStandardizedMovieId(r.data as TMDbMovie); } catch { return null; } })
          .filter((id): id is string => id !== null);
        const ratings = movieIds.length
          ? await movieService.getMovieRatingsBatch(movieIds).catch(() => new Map<string, number>())
          : new Map<string, number>();

        // Single batched update — React 18 auto-batches these so only one re-render fires.
        setRecentMovies(filteredMovies);
        setRecentMoviesCommunityRatings(ratings);
        setRecentWatchlists(watchlists);
        setRecentUsers(users);
        setRecentPeople(people);
      } catch (error) {
        console.error('Error loading recent items:', error);
      } finally {
        setLoadingRecent(false);
      }
    };

    loadRecentItems();
  }, [user?.uid]);

  // Enhanced search function with pagination support and caching
  const performSearch = useCallback(async (
    query: string,
    tabToSearch: number,
    page: number = 1,
    loadMore: boolean = false,
    skipCache: boolean = false
  ) => {
    const trimmedQuery = query.trim();
    if (!trimmedQuery || trimmedQuery.length < 2) return;

    const perfStart = performance.now();

    // PHASE 2: Analyze query for logging / future use (no auto enhanced mode)
    const queryAnalysis = analyzeSearchQuery(trimmedQuery);
    console.log(`🧠 Query analysis:`, {
      shouldUseEnhanced: queryAnalysis.shouldUseEnhanced,
      reason: queryAnalysis.reason,
      confidence: queryAnalysis.confidence,
      patterns: queryAnalysis.patterns
    });

    // Server auto-escalates to enhanced strategies when warranted — client always
    // sends the basic request.
    const effectiveUseEnhanced = false;

    // 1. Cache check first — no side effects, safe to run before any abort.
    if (!loadMore && !skipCache && page === 1) {
      const cached = getCachedResults(trimmedQuery, tabToSearch, effectiveUseEnhanced);
      if (cached) {
        console.log('⚡ SearchPage: Using cached results');
        setSearchResults(cached.results);
        setHasSearched(true);
        setCurrentSearchQuery(trimmedQuery);
        setSearchLoading(false);
        setSearchUpdating(false);
        void reportSearchResultsPerf(getSearchResultsPerfPageName(tabToSearch), perfStart, true);
        return;
      }
    }

    // 2. Dedup check BEFORE abort — if the same query+tab is already in flight, let
    //    it complete rather than killing it and returning with no request running.
    //    Bug fixed: abort() was previously called first, which killed the live request,
    //    then the dedup check returned early — leaving the user with no active request.
    const dedupeKey = !loadMore && page === 1
      ? `${trimmedQuery.toLowerCase()}_tab${tabToSearch}_${effectiveUseEnhanced ? 'enh' : 'basic'}`
      : null;
    if (dedupeKey && inFlightSearchRef.current.has(dedupeKey)) {
      console.log(`🔁 SearchPage: dedupe — "${dedupeKey}" already in flight, not aborting`);
      return;
    }

    // 3. Only abort the previous request now that we know we're starting a new one
    //    (it must have been for a different query or tab).
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    // Track this request with a unique ID to ignore stale responses
    const currentRequestId = ++searchRequestIdRef.current;

    console.log(
      `🔍 SearchPage: search "${trimmedQuery}" tab ${tabToSearch} page ${page} (request #${currentRequestId})`
    );

    if (dedupeKey) {
      inFlightSearchRef.current.add(dedupeKey);
    }

    if (!loadMore) {
      setHasSearched(true);
      setCurrentSearchQuery(trimmedQuery);
      onSearchStateChange?.(trimmedQuery, tabToSearch);

      // If there are already results for this tab, keep them visible with a subtle
      // progress bar (searchUpdating) rather than replacing everything with a skeleton.
      // Only show the full skeleton (searchLoading) when there's nothing to show yet.
      const tabKeyMap: Record<number, string> = {
        [TAB_MOVIES]: 'movies',
        [TAB_WATCHLISTS]: 'watchlists',
        [TAB_USERS]: 'profiles',
        [TAB_PEOPLE]: 'people',
      };
      const tabKey = tabKeyMap[tabToSearch];
      const currentTabResults = tabKey ? ((searchResultsRef.current as unknown) as Record<string, unknown[]>)[tabKey] : undefined;
      const hasExistingResults = (currentTabResults?.length ?? 0) > 0;
      if (hasExistingResults) {
        setSearchUpdating(true);
      } else {
        setSearchLoading(true);
      }
    }

    try {
      let movieResults: TMDbMovie[] = [];
      let watchlistResults: any[] = [];
      let peopleResults: any[] = [];
      let profileResults: UserProfileType[] = [];
      let paginationInfo: any = {};

      switch (tabToSearch) {
        case TAB_MOVIES: {
          try {
            const result = await SearchService.searchMoviesWithPagination(
              trimmedQuery,
              page,
              effectiveUseEnhanced
            );
            movieResults = loadMore ? result.movies : result.movies;

            const newMatchInfo = new Map<string, { matchedFields: string[]; relevanceScore: number }>();
            if (result.enhancedResults) {
              result.enhancedResults.forEach(enhancedResult => {
                const movieId = enhancedResult.movie.id ? `tmdb_${enhancedResult.movie.id}` : (enhancedResult.movie.imdbID || 'unknown');
                newMatchInfo.set(movieId, {
                  matchedFields: enhancedResult.matchedFields,
                  relevanceScore: enhancedResult.relevanceScore
                });
              });
            }

            paginationInfo.movies = {
              current: page,
              total: result.totalResults,
              hasMore: result.hasMore
            };
            paginationInfo.newMatchInfo = newMatchInfo;
          } catch (error) {
            console.error('Error in enhanced movie search:', error);
            movieResults = [];
            paginationInfo.movies = { current: page, total: 0, hasMore: false };
          }
          break;
        }

        case TAB_WATCHLISTS: {
          const traceLabel = TraceNames.SEARCH_WATCHLISTS;
          const wlTraceStart = performance.now();
          await startTrace(traceLabel, {
            q_len: String(Math.min(trimmedQuery.length, 200)),
            public_pool: String(allWatchlists?.length ?? 0),
          });
          let wlResultCount = 0;
          try {
            const allResults = await SearchService.searchWatchlistsWithContext(
              trimmedQuery,
              searchWatchlists,
              allWatchlists
            );
            wlResultCount = allResults.length;
            const startIndex = (page - 1) * 8;
            const endIndex = startIndex + 8;
            watchlistResults = allResults.slice(startIndex, endIndex);

            paginationInfo.watchlists = {
              current: page,
              total: allResults.length,
              hasMore: endIndex < allResults.length
            };
          } catch (error) {
            console.error('Error searching watchlists:', error);
            watchlistResults = [];
            paginationInfo.watchlists = { current: page, total: 0, hasMore: false };
          } finally {
            await stopTrace(
              traceLabel,
              {
                duration_ms: Math.round(performance.now() - wlTraceStart),
                result_count: wlResultCount,
              },
              { page: String(page) }
            );
          }
          break;
        }

        case TAB_USERS: {
          const usersTraceLabel = TraceNames.SEARCH_USERS;
          const usersTraceStart = performance.now();
          await startTrace(usersTraceLabel, { q_len: String(Math.min(trimmedQuery.length, 200)) });
          let profileTotal = 0;
          try {
            const allProfiles = await SearchService.searchUsers(trimmedQuery);
            profileTotal = allProfiles.length;
            const startIndex = (page - 1) * 8;
            const endIndex = startIndex + 8;
            profileResults = allProfiles.slice(startIndex, endIndex);

            paginationInfo.profiles = {
              current: page,
              total: allProfiles.length,
              hasMore: endIndex < allProfiles.length
            };
          } catch (error) {
            console.error('Error searching users:', error);
            profileResults = [];
            paginationInfo.profiles = { current: page, total: 0, hasMore: false };
          } finally {
            await stopTrace(
              usersTraceLabel,
              {
                duration_ms: Math.round(performance.now() - usersTraceStart),
                result_count: profileTotal,
              },
              { page: String(page) }
            );
          }
          break;
        }

        case TAB_PEOPLE: {
          const peopleTraceLabel = TraceNames.SEARCH_PEOPLE;
          const peopleTraceStart = performance.now();
          await startTrace(peopleTraceLabel, { q_len: String(Math.min(trimmedQuery.length, 200)) });
          let peopleTotal = 0;
          try {
            const allPeople = await SearchService.searchPeople(trimmedQuery);
            peopleTotal = allPeople.length;
            const startIndex = (page - 1) * 8;
            const endIndex = startIndex + 8;
            peopleResults = allPeople.slice(startIndex, endIndex);

            paginationInfo.people = {
              current: page,
              total: allPeople.length,
              hasMore: endIndex < allPeople.length
            };
          } catch (error) {
            console.error('Error searching people:', error);
            peopleResults = [];
            paginationInfo.people = { current: page, total: 0, hasMore: false };
          } finally {
            await stopTrace(
              peopleTraceLabel,
              {
                duration_ms: Math.round(performance.now() - peopleTraceStart),
                result_count: peopleTotal,
              },
              { page: String(page) }
            );
          }
          break;
        }

        default:
          break;
      }

      // Discard results from a superseded request (user kept typing after this fired).
      if (currentRequestId !== searchRequestIdRef.current) {
        return;
      }

      setSearchResults(prev => {
        let newMovieMatchInfo = prev.movieMatchInfo;
        if (paginationInfo.newMatchInfo) {
          if (loadMore && tabToSearch === TAB_MOVIES) {
            newMovieMatchInfo = new Map([...prev.movieMatchInfo, ...paginationInfo.newMatchInfo]);
          } else {
            newMovieMatchInfo = paginationInfo.newMatchInfo;
          }
        } else if (!loadMore && tabToSearch === TAB_MOVIES) {
          newMovieMatchInfo = new Map();
        }

        return {
          ...prev,
          movies: tabToSearch === TAB_MOVIES
            ? (loadMore ? [...(prev.movies || []), ...movieResults] : movieResults)
            : prev.movies,
          watchlists: tabToSearch === TAB_WATCHLISTS
            ? (loadMore ? [...(prev.watchlists || []), ...watchlistResults] : watchlistResults)
            : prev.watchlists,
          people: tabToSearch === TAB_PEOPLE
            ? (loadMore ? [...(prev.people || []), ...peopleResults] : peopleResults)
            : prev.people,
          profiles: tabToSearch === TAB_USERS
            ? (loadMore ? [...(prev.profiles || []), ...profileResults] : profileResults)
            : prev.profiles,
          movieMatchInfo: newMovieMatchInfo,
          pagination: {
            ...prev.pagination,
            ...paginationInfo
          }
        };
      });

      // Save to recent searches (only on initial search, not pagination)
      if (!loadMore && user && page === 1) {
        let searchType: 'movie' | 'person' | 'user' = 'movie';
        let resultCount = 0;

        switch (tabToSearch) {
          case TAB_MOVIES:
            searchType = 'movie';
            resultCount = paginationInfo.movies?.total || 0;
            break;
          case TAB_PEOPLE:
            searchType = 'person';
            resultCount = paginationInfo.people?.total || 0;
            break;
          case TAB_USERS:
            searchType = 'user';
            resultCount = paginationInfo.profiles?.total || 0;
            break;
          case TAB_WATCHLISTS:
            // Watchlists are not a standard search type, default to movie
            searchType = 'movie';
            resultCount = paginationInfo.watchlists?.total || 0;
            break;
        }

        dataService.saveRecentSearch(user, trimmedQuery, searchType, resultCount)
          .catch(err => {
            console.error('Error saving recent search:', err);
          });
      }

      // Cache tab-specific search results (only for initial searches)
      if (!loadMore && page === 1) {
        const tabCacheResults = {
          movies: tabToSearch === TAB_MOVIES ? movieResults : [],
          watchlists: tabToSearch === TAB_WATCHLISTS ? watchlistResults : [],
          people: tabToSearch === TAB_PEOPLE ? peopleResults : [],
          profiles: tabToSearch === TAB_USERS ? profileResults : [],
          movieMatchInfo: paginationInfo.newMatchInfo || new Map(),
          pagination: {
            movies: paginationInfo.movies || { current: 1, total: 0, hasMore: false },
            watchlists: paginationInfo.watchlists || { current: 1, total: 0, hasMore: false },
            people: paginationInfo.people || { current: 1, total: 0, hasMore: false },
            profiles: paginationInfo.profiles || { current: 1, total: 0, hasMore: false }
          },
          breakdown: {
            movies: { total: paginationInfo.movies?.total || 0, returned: movieResults.length },
            watchlists: { total: paginationInfo.watchlists?.total || 0, returned: watchlistResults.length },
            profiles: { total: paginationInfo.profiles?.total || 0, returned: profileResults.length },
            people: { total: paginationInfo.people?.total || 0, returned: peopleResults.length }
          }
        };
        cacheResults(trimmedQuery, tabToSearch, tabCacheResults, undefined, effectiveUseEnhanced);
      }

      if (!loadMore && page === 1) {
        void reportSearchResultsPerf(getSearchResultsPerfPageName(tabToSearch), perfStart, false);

        // PHASE 2: Track search analytics for all tabs
        const tabName: 'movies' | 'watchlists' | 'users' | 'people' | 'all' =
          tabToSearch === TAB_MOVIES ? 'movies' :
          tabToSearch === TAB_WATCHLISTS ? 'watchlists' :
          tabToSearch === TAB_USERS ? 'users' :
          tabToSearch === TAB_PEOPLE ? 'people' : 'all';

        const resultCount =
          tabToSearch === TAB_MOVIES ? movieResults.length :
          tabToSearch === TAB_WATCHLISTS ? watchlistResults.length :
          tabToSearch === TAB_USERS ? profileResults.length :
          tabToSearch === TAB_PEOPLE ? peopleResults.length : 0;

        enhancedSearchAnalytics.trackSearch({
          query: trimmedQuery,
          tab: tabName,
          mode: effectiveUseEnhanced ? 'enhanced' : 'basic',
          resultCount,
          loadTimeMs: Math.round(performance.now() - perfStart),
          trigger: effectiveUseEnhanced ? 'user-toggle' : undefined,
        });
      }
    } catch (err: any) {
      // Don't clear results for aborted requests — they were superseded by a newer search
      // and that newer search is still in flight. Clearing here would flash empty results.
      const isAbort = err?.name === 'AbortError' || err?.code === 20;
      const isLatest = currentRequestId === searchRequestIdRef.current;
      if (!isAbort && isLatest && !loadMore) {
        console.error('Search error:', err);
        setSearchResults(prev => ({
          ...prev,
          movies: tabToSearch === TAB_MOVIES ? [] : prev.movies,
          watchlists: tabToSearch === TAB_WATCHLISTS ? [] : prev.watchlists,
          people: tabToSearch === TAB_PEOPLE ? [] : prev.people,
          profiles: tabToSearch === TAB_USERS ? [] : prev.profiles,
          movieMatchInfo: tabToSearch === TAB_MOVIES ? new Map() : prev.movieMatchInfo,
          pagination: {
            ...prev.pagination,
            movies: tabToSearch === TAB_MOVIES ? { current: 1, total: 0, hasMore: false } : prev.pagination.movies,
            watchlists: tabToSearch === TAB_WATCHLISTS ? { current: 1, total: 0, hasMore: false } : prev.pagination.watchlists,
            people: tabToSearch === TAB_PEOPLE ? { current: 1, total: 0, hasMore: false } : prev.pagination.people,
            profiles: tabToSearch === TAB_USERS ? { current: 1, total: 0, hasMore: false } : prev.pagination.profiles
          }
        }));
      } else if (isAbort) {
        console.log(`🚫 SearchPage: request #${currentRequestId} aborted (superseded)`);
      }
    } finally {
      // Only clear loading state if this is still the latest request.
      // An aborted request's finally must not hide the skeleton for the newer in-flight request.
      if (currentRequestId === searchRequestIdRef.current) {
        setSearchLoading(false);
        setSearchUpdating(false);
        setLoadingMore(false);
      }
      if (dedupeKey) {
        inFlightSearchRef.current.delete(dedupeKey);
      }
    }
  }, [allWatchlists, searchWatchlists, user, getCachedResults, cacheResults, onSearchStateChange]);

  // Load More handler
  const handleLoadMore = async () => {
    const currentTab = activeSearchTab;

    const tabKeyMap: Record<number, keyof typeof searchResults.pagination> = {
      [TAB_MOVIES]: 'movies',
      [TAB_WATCHLISTS]: 'watchlists',
      [TAB_USERS]: 'profiles',
      [TAB_PEOPLE]: 'people'
    };

    const tabKey = tabKeyMap[currentTab];
    if (!tabKey) return;

    const pagination = searchResults.pagination[tabKey];
    if (loadingMore || !pagination.hasMore) return;

    setLoadingMore(true);
    const nextPage = pagination.current + 1;

    await performSearch(currentSearchQuery, currentTab, nextPage, true);
  };

  const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    console.log('🔍 SearchPage: Input changed:', { value, length: value.length });
    setSearchQuery(value);

    // Clear search results when input is cleared
    if (value.length === 0) {
      setHasSearched(false);
      setSearchLoading(false);
      setSearchUpdating(false);
      setSearchResults({
        movies: [],
        watchlists: [],
        people: [],
        profiles: [],
        movieMatchInfo: new Map(),
        pagination: {
          movies: { current: 1, total: 0, hasMore: false },
          watchlists: { current: 1, total: 0, hasMore: false },
          people: { current: 1, total: 0, hasMore: false },
          profiles: { current: 1, total: 0, hasMore: false }
        },
        breakdown: {
          movies: { total: 0, returned: 0 },
          watchlists: { total: 0, returned: 0 },
          profiles: { total: 0, returned: 0 },
          people: { total: 0, returned: 0 }
        }
      });
      setCurrentSearchQuery('');
    } else if (value.trim().length >= 2 && !hasSearched) {
      // Show skeleton immediately — before the 450ms debounce fires — so the user
      // gets instant visual feedback that something is happening.
      setHasSearched(true);
      setSearchLoading(true);
    }
  };

  // Debounced search effect - balanced for stability
  useEffect(() => {
    if (!searchQuery.trim() || searchQuery.length < 2) {
      return;
    }

    // Skip if handleSearchTabChange already fired this exact search immediately.
    // The tab change updates activeSearchTab (triggering this effect) but also calls
    // performSearch directly — firing 450ms later would duplicate the request.
    const effectKey = `${searchQuery.trim().toLowerCase()}_tab${activeSearchTab}`;
    if (effectKey === lastImmediateSearchRef.current) {
      lastImmediateSearchRef.current = ''; // consume once
      lastDebouncedSearchKeyRef.current = effectKey;
      return;
    }

    // Skip if we already debounced this exact query+tab. Happens when the user
    // comes back to /search from a movie detail: performSearch's identity
    // changed (its context deps updated while the user was away), but the
    // query/tab they were looking at hasn't. Re-firing here would cache-restore
    // page 1 and wipe the loaded-more pages, shrinking the list and breaking
    // the preserved scrollTop.
    if (effectKey === lastDebouncedSearchKeyRef.current) {
      return;
    }

    // 450ms debounce: tuned to keep TMDB request volume below the rate limit while
    // staying responsive. Each in-flight request is aborted (and treated as stale)
    // when the next one fires inside performSearch.
    const timeoutId = setTimeout(() => {
      lastDebouncedSearchKeyRef.current = effectKey;
      performSearch(searchQuery.trim(), activeSearchTab);
    }, 450);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [searchQuery, activeSearchTab, performSearch]);

  const handleSearchTabChange = (newValue: number) => {
    setActiveSearchTab(newValue);

    // Maintain search focus state when switching tabs
    if (!isSearchFocused) {
      setIsSearchFocused(true);
    }

    // Use the current search query or the search input value
    const queryToSearch = currentSearchQuery.trim() || searchQuery.trim();
    if (queryToSearch) {
      // Show loading immediately so stale results from the previous tab don't flash.
      // performSearch will clear this via finally once results arrive (or cache hits).
      setSearchLoading(true);
      setSearchUpdating(false);
      // Mark this as an immediate search so the debounce effect skips its 450ms re-fire.
      lastImmediateSearchRef.current = `${queryToSearch.trim().toLowerCase()}_tab${newValue}`;
      performSearch(queryToSearch, newValue);
    } else {
      // If no query but tabs are being interacted with, maintain search context
      setHasSearched(false);
      setSearchResults({
        movies: [],
        watchlists: [],
        people: [],
        profiles: [],
        movieMatchInfo: new Map(),
        pagination: {
          movies: { current: 1, total: 0, hasMore: false },
          watchlists: { current: 1, total: 0, hasMore: false },
          people: { current: 1, total: 0, hasMore: false },
          profiles: { current: 1, total: 0, hasMore: false }
        },
        breakdown: {
          movies: { total: 0, returned: 0 },
          watchlists: { total: 0, returned: 0 },
          profiles: { total: 0, returned: 0 },
          people: { total: 0, returned: 0 }
        }
      });
    }

    // Ensure search input maintains focus after tab change
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 50);
  };

  useSwipeTab(activeSearchTab, [TAB_MOVIES, TAB_WATCHLISTS, TAB_USERS, TAB_PEOPLE], handleSearchTabChange);

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults({
      movies: [],
      watchlists: [],
      people: [],
      profiles: [],
      movieMatchInfo: new Map(),
      pagination: {
        movies: { current: 1, total: 0, hasMore: false },
        watchlists: { current: 1, total: 0, hasMore: false },
        people: { current: 1, total: 0, hasMore: false },
        profiles: { current: 1, total: 0, hasMore: false }
      },
      breakdown: {
        movies: { total: 0, returned: 0 },
        watchlists: { total: 0, returned: 0 },
        profiles: { total: 0, returned: 0 },
        people: { total: 0, returned: 0 }
      }
    });
    setHasSearched(false);
    setCurrentSearchQuery('');
    setIsSearchFocused(false);
  };


  const handleSearchFocus = () => {
    setIsSearchFocused(true);
    setSearchActive(true);
    // Pre-warm the Cloud Function so it's ready by the time the debounce fires.
    // On Capacitor this cuts first-search cold-start latency from ~3-5s to <1s.
    if (!hasSearched) {
      SearchService.warmUp();
    }
  };

  // Back button: from Active search → Explore; from Explore → caller's onBack.
  const handleBackPressed = () => {
    if (searchActive) {
      clearSearch();
      setSearchActive(false);
      inputRef.current?.blur();
      return;
    }
    onBack();
  };

  const handleSearchBlur = () => {
    setTimeout(() => {
      setIsSearchFocused(false);
    }, 200);
  };

  // Voice search — Web Speech API only (mic hidden on Capacitor)
  useEffect(() => {
    if (!Capacitor.isNativePlatform() && typeof window !== 'undefined' && ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) {
      const SpeechRecognition = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition;
      const recognition = new SpeechRecognition();

      recognition.continuous = true; // Allow continuous listening
      recognition.interimResults = true; // Show interim results while speaking
      recognition.lang = 'en-US';

      recognition.onstart = () => {
        setIsListening(true);
        console.log('🎤 Web voice search started');
      };

      recognition.onresult = (event: any) => {
        let finalTranscript = '';
        let interimTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }

        if (finalTranscript) {
          setSearchQuery(finalTranscript.trim());
          console.log('🎤 Final transcript:', finalTranscript);

          // Auto-search on final result
          if (finalTranscript.trim()) {
            performSearch(finalTranscript.trim(), activeSearchTab);
          }
        } else if (interimTranscript) {
          setSearchQuery(interimTranscript.trim());
          console.log('🎤 Interim transcript:', interimTranscript);
        }
      };

      recognition.onerror = (event: any) => {
        console.error('🎤 Speech recognition error:', event.error);
        setIsListening(false);

        if (event.error === 'not-allowed') {
          showError('Microphone access denied. Please allow microphone access and try again.');
        } else if (event.error === 'no-speech') {
          console.log('🎤 No speech detected, continuing to listen...');
        } else {
          showError(`Voice search error: ${event.error}. Please try again.`);
        }
      };

      recognition.onend = () => {
        setIsListening(false);
        console.log('🎤 Web voice search ended');
      };

      setSpeechRecognition(recognition);
    }
  }, [activeSearchTab, performSearch]);

  const handleVoiceSearch = async () => {
    if (!showVoiceSearch) {
      return;
    }

    // Web only (native voice search disabled)
    if (!speechRecognition) {
      showWarning('Voice search is not supported in your browser. Please try Chrome, Safari, or Edge.');
      return;
    }

    if (isListening) {
      console.log('🎤 Stopping web voice search manually');
      speechRecognition.stop();
    } else {
      console.log('🎤 Starting web voice search manually');
      setSearchQuery('');
      speechRecognition.start();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      clearSearch();
    }
  };

  // Helper to track search result clicks with position
  const trackResultClick = useCallback((
    resultType: 'movie' | 'watchlist' | 'user' | 'person',
    resultId: string,
    position: number = 0
  ) => {
    const query = currentSearchQuery || searchQuery;
    if (query.trim()) {
      const totalResults = searchResults.movies.length + searchResults.watchlists.length +
        searchResults.profiles.length + searchResults.people.length;
      searchAnalytics.trackClick(query, resultType, resultId, position, totalResults);
    }
  }, [currentSearchQuery, searchQuery, searchResults]);

  const handleMovieClick = (movieId: string, position: number = 0, movieData?: any) => {
    console.log('🎬 SearchPage: Movie clicked:', movieId);

    // Stash movie preview data if available
    if (movieData) {
      stashMovieDetailNavPreviewFromMovie(
        movieId,
        movieData,
        () => {
          // Return poster URL for instant display
          if (movieData.poster_path && movieData.poster_path !== 'N/A') {
            if (movieData.poster_path.startsWith('http')) {
              return movieData.poster_path;
            }
            return `https://image.tmdb.org/t/p/w500${movieData.poster_path}`;
          }
          if (movieData.Poster && movieData.Poster !== 'N/A') {
            return movieData.Poster;
          }
          return null;
        }
      );
    }

    try {
      sessionStorage.setItem(
        PERF_SEARCH_TO_MOVIE_START_KEY,
        JSON.stringify({ start: performance.now(), wall: Date.now() })
      );
    } catch {
      /* storage unavailable */
    }
    trackResultClick('movie', movieId, position);
    openMovieQuickView(movieId);
    console.log('🎬 SearchPage: openMovieQuickView called successfully');
  };

  const handleWatchlistClickFromSearch = (watchlistId: string, watchlist: any, position: number = 0) => {
    // Track analytics
    trackResultClick('watchlist', watchlistId, position);
    // 🚀 CRITICAL FIX: For collaborative watchlists, determine the correct target user ID
    // If current user is viewing a collaborative watchlist, we need to pass the current user ID
    // as the target so that WatchlistDetail handles permissions correctly

    const isCollaborativeList = watchlist.collaborators &&
      watchlist.collaborators.includes(user?.uid) &&
      watchlist.createdBy !== user?.uid;

    if (isCollaborativeList && user) {
      // For collaborative lists, pass current user ID so WatchlistDetail shows proper context
      console.log('🔄 SearchPage: Navigating to collaborative watchlist for current user:', {
        watchlistId,
        currentUserId: user.uid,
        actualCreator: watchlist.createdBy,
        isCollaborative: true
      });
      onWatchlistSelect(watchlistId, 0, user.uid);
    } else if (watchlist.createdBy && watchlist.createdBy !== user?.uid) {
      // For other users' watchlists, pass the creator's ID
      console.log('🔄 SearchPage: Navigating to other user watchlist:', {
        watchlistId,
        creatorId: watchlist.createdBy,
        isOtherUser: true
      });
      onWatchlistSelect(watchlistId, 0, watchlist.createdBy);
    } else {
      // For own watchlists, no target user ID needed
      onWatchlistSelect(watchlistId, 0);
    }
  };

  const handleUserProfileClick = (userId: string, position: number = 0) => {
    trackResultClick('user', userId, position);
    onUserProfileSelect(userId);
  };

  const handlePersonClick = (
    person: {
      id: string | number;
      name: string;
      profile_path?: string | null;
      known_for_department?: string | null;
    },
    position: number = 0
  ) => {
    const personId = String(person.id);
    trackResultClick('person', personId, position);
    stashPersonDetailNavPreviewFromPerson(personId, person, () => {
      if (person.profile_path) {
        if (person.profile_path.startsWith('http')) return person.profile_path;
        return `https://image.tmdb.org/t/p/w500${person.profile_path}`;
      }
      return null;
    });
    onPersonSelect(personId);
  };

  // Get creator and collaboration information for watchlists
  const getCreatorInfo = (watchlist: any) => {
    const creatorId = watchlist?.createdBy;
    const creatorDisplayName = creatorId
      ? getUserDisplayName(creatorId, watchlist?.createdByName || 'Unknown User')
      : (watchlist?.createdByName || 'Anonymous User');
    const creatorAvatar = creatorId ? getUserAvatar(creatorId) : watchlist?.createdByAvatar;

    // Check if current user is a collaborator but not the creator
    const isCurrentUserCollaborator = watchlist?.collaborators &&
      watchlist.collaborators.includes(user?.uid) &&
      watchlist.createdBy !== user?.uid;

    if (isCurrentUserCollaborator) {
      // For collaborative watchlists, show "You + Creator" format
      return {
        name: `You + ${creatorDisplayName}`,
        avatar: user?.photoURL // Show current user's avatar for collaborative lists
      };
    }

    if (watchlist?.createdByName) {
      return {
        name: watchlist.createdByName,
        avatar: watchlist.createdByAvatar || creatorAvatar
      };
    }

    if (watchlist?.createdBy === user?.uid && user) {
      return {
        name: user.displayName || 'You',
        avatar: user.photoURL
      };
    }

    return {
      name: creatorDisplayName,
      avatar: creatorAvatar
    };
  };

  const renderRecentSection = () => {
    if (searchQuery.trim()) return null;

    const hasRecentActivity = recentMovies.length > 0 || recentWatchlists.length > 0 || recentUsers.length > 0 || recentPeople.length > 0;

    const heading = (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{
          fontWeight: 600,
          fontSize: { xs: 16, sm: 18 },
          color: theme.palette.text.primary,
        }}>
          Recent
        </Typography>
        {hasRecentActivity && (
          <Button
            size="small"
            onClick={handleClearRecentActivity}
            sx={{
              textTransform: 'none',
              fontSize: 13,
              fontWeight: 600,
              color: theme.palette.primary.main,
              '&:hover': {
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
              }
            }}
          >
            Clear All
          </Button>
        )}
      </Box>
    );

    if (activeSearchTab === TAB_MOVIES) {
      return (
        <Box sx={{ mb: 4 }}>
          {heading}
          {loadingRecent ? (
            <Skeleton
              variant="rounded"
              width="100%"
              height={200}
              sx={{ borderRadius: 2, bgcolor: skBg }}
            />
          ) : recentMovies.length > 0 ? (
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(3, minmax(0, 1fr))',
                sm: 'repeat(4, minmax(0, 1fr))',
                md: 'repeat(5, minmax(0, 1fr))',
                lg: 'repeat(6, minmax(0, 1fr))',
              },
              gap: { xs: 1, sm: 1.5, md: 2 },
            }}>
              {recentMovies.map((recentMovie) => {
                const movie = recentMovie.data;
                const movieId = recentMovie.id;

                // Skip movies with incomplete data (missing title or poster)
                // This happens when Firebase has stale/incomplete movie records or TMDB fetch failed
                const hasTitle = movie && (movie.Title || movie.title);
                const getPosterUrl = () => {
                  if (movie.poster_path && movie.poster_path !== 'N/A') {
                    if (movie.poster_path.startsWith('http')) {
                      return movie.poster_path;
                    }
                    return tmdbService.getGridPoster(movie.poster_path);
                  }
                  if (movie.Poster && movie.Poster !== 'N/A') {
                    if (movie.Poster.startsWith('http')) {
                      return movie.Poster;
                    }
                    return tmdbService.getGridPoster(movie.Poster);
                  }
                  return null;
                };

                const posterUrl = getPosterUrl();
                const hasPoster = !!posterUrl;

                // Robust placeholder detection (e.g. title is just the ID)
                const isPlaceholderTitle = hasTitle === 'Movie' || (hasTitle && hasTitle.length > 20 && (hasTitle.includes('_') || hasTitle.includes('-')));

                if (!hasTitle || isPlaceholderTitle || !hasPoster) {
                  console.warn('SearchPage: Filtering broken movie in Recent:', {
                    id: movieId,
                    title: hasTitle,
                    hasPoster,
                    isPlaceholder: isPlaceholderTitle
                  });
                  return null;
                }
                const movieTitle = movie.Title || movie.title || 'Unknown Movie';
                const movieYear = movie.Year || movie.year || (movie.release_date ? movie.release_date.split('-')[0] : '');
                const standardizedMovieId = getStandardizedMovieId(movie);
                const userMovieRow = getUserMovieData(movieId) ?? getUserMovieData(standardizedMovieId);
                const rowIsWatched = userMovieRow?.isWatched === true;
                const rowUserRating = userMovieRow?.rating;
                const rowCommunityAvg = recentMoviesCommunityRatings.get(standardizedMovieId);

                return (
                  <Box
                    key={movieId}
                    sx={{
                      cursor: 'pointer',
                      position: 'relative',
                      minWidth: 0, // Ensure box can shrink within grid cell
                      '&:hover': {
                        transform: 'scale(1.02)',
                      },
                      transition: 'all 0.2s ease-in-out',
                    }}
                    onClick={() => handleMovieClick(movieId, 0, movie)}
                  >
                    <Box sx={{
                      width: '100%',
                      aspectRatio: '2/3',
                      borderRadius: 2,
                      overflow: 'hidden',
                      position: 'relative',
                      bgcolor: theme.palette.mode === 'dark' ? '#3A3A3C' : '#F0F0F0',
                      boxShadow: theme.palette.mode === 'dark'
                        ? '0 4px 12px rgba(0,0,0,0.4)'
                        : '0 4px 12px rgba(0,0,0,0.1)',
                    }}>
                      <ImageShimmer
                        src={posterUrl}
                        alt={movieTitle}
                        width="100%"
                        height="100%"
                        objectFit="cover"
                        borderRadius={2}
                      />

                      <Box sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.6) 50%, transparent 100%)',
                        padding: { xs: 1, sm: 1.5 },
                        paddingTop: { xs: 3, sm: 4 },
                        zIndex: 3,
                      }}>
                        <Typography variant="body1" sx={{
                          fontWeight: 600,
                          color: '#FFFFFF',
                          fontSize: { xs: '13px', sm: '14px' },
                          mb: -0.5,
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          textOverflow: 'ellipsis',
                          fontFamily: font(),
                          lineHeight: 1.3,
                          textShadow: '0 1px 3px rgba(0,0,0,0.8)',
                          wordBreak: 'break-word', // Break long words like IDs
                        }}>
                          {movieTitle}
                        </Typography>

                        <Typography variant="caption" sx={{
                          color: 'rgba(255,255,255,0.85)',
                          fontSize: { xs: '11px', sm: '12px' },
                          fontWeight: 500,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '100%'
                        }}>
                          {movieYear}
                        </Typography>

                        {rowIsWatched && rowUserRating != null ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                            <Star sx={{ fontSize: { xs: 12, sm: 14 }, color: '#FFD700', flexShrink: 0 }} />
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'rgba(255,255,255,0.9)',
                                fontSize: { xs: '11px', sm: '12px' },
                                fontWeight: 600,
                              }}
                            >
                              {rowUserRating}
                            </Typography>
                          </Box>
                        ) : !rowIsWatched && rowCommunityAvg != null ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                            <WatchlistCommunityRatingIcon size={14} forDarkOverlay />
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'rgba(255,255,255,0.9)',
                                fontSize: { xs: '11px', sm: '12px' },
                                fontWeight: 600,
                              }}
                            >
                              {rowCommunityAvg.toFixed(1)}
                            </Typography>
                          </Box>
                        ) : null}
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ) : (
            <EmptyState message="No recent movies" />
          )}
        </Box>
      );
    }

    if (activeSearchTab === TAB_WATCHLISTS) {
      const watchlistCards = recentWatchlists
        .map(recentWatchlist => getWatchlistDisplayData(recentWatchlist))
        .filter((watchlist): watchlist is NonNullable<typeof watchlist> => Boolean(watchlist));

      return (
        <Box sx={{ mb: 4 }}>
          {heading}
          {loadingRecent ? (
            <Skeleton
              variant="rounded"
              width="100%"
              height={150}
              sx={{ borderRadius: 2, bgcolor: skBg }}
            />
          ) : watchlistCards.length > 0 ? (
            <Masonry
              breakpointCols={{
                default: 2,
                900: 1,
                600: 1
              }}
              className="masonry-grid"
              columnClassName="masonry-grid_column"
            >
              {watchlistCards.map((watchlist) => {
                const creatorInfo = getCreatorInfo(watchlist);
                const moviePosters = (watchlist.movieDetails || [])
                  .slice(0, 3)
                  .map((movie: any) => movie.poster || movie.Poster || '')
                  .filter(Boolean);

                return (
                  <Box key={watchlist.id} sx={{ mb: 1.5 }}>
                    <WatchlistWidget
                      watchlistType={watchlist.id}
                      user={user}
                      targetUserId={watchlist.createdBy}
                      isOtherUserWatchlist={watchlist.createdBy !== user?.uid}
                      moviePosters={moviePosters}
                      variant="compact"
                      onClick={() => handleWatchlistClickFromSearch(watchlist.id, watchlist)}
                      watchlistId={watchlist.id}
                      isOwned={watchlist.createdBy === user?.uid}
                      isFollowing={false}
                      showFollowButton={false}
                      onAuthorClick={undefined}
                      externalWatchlistData={{
                        name: watchlist.name,
                        description: watchlist.description,
                        movieDetails: watchlist.movieDetails || [],
                        isPrivate: watchlist.isPrivate || false,
                        createdBy: watchlist.createdBy || user?.uid || '',
                        createdByName: creatorInfo.name,
                        createdByAvatar: creatorInfo.avatar,
                        collaborators: watchlist.collaborators || [],
                        collaboratorProfiles: (watchlist.collaborators || []).map((collabId: string) => ({
                          uid: collabId,
                          displayName: getUserDisplayName(collabId, '') || 'User',
                          photoURL: getUserAvatar(collabId)
                        })),
                        followerCount: watchlist.followerCount || 0,
                        updatedAt: watchlist.updatedAt,
                        createdAt: watchlist.createdAt,
                      }}
                    />
                  </Box>
                );
              })}
            </Masonry>
          ) : (
            <EmptyState message="No recent watchlists" />
          )}
        </Box>
      );
    }

    if (activeSearchTab === TAB_USERS) {
      return (
        <Box sx={{ mb: 4 }}>
          {heading}
          {loadingRecent ? (
            <Skeleton
              variant="rounded"
              width="100%"
              height={150}
              sx={{ borderRadius: 2, bgcolor: skBg }}
            />
          ) : recentUsers.length > 0 ? (
            <Masonry
              breakpointCols={{
                default: 2,
                900: 1,
                600: 1
              }}
              className="masonry-grid"
              columnClassName="masonry-grid_column"
            >
              {recentUsers.map((recentUser) => (
                <Box key={recentUser.id} sx={{ mb: 1.5 }}>
                  <UserListingCard
                    id={recentUser.id}
                    name={getUserDisplayName(recentUser.id, recentUser.data.displayName || 'Anonymous User')}
                    username={`@${getUserUsername(recentUser.id, recentUser.data.username || 'user')}`}
                    avatar={getUserAvatar(recentUser.id) || recentUser.data.photoURL || ''}
                    stats={{
                      followers: recentUser.data.stats?.followersCount || 0,
                      watchlists: recentUser.data.stats?.listsCreated || 0,
                    }}
                    isFollowing={isUserFollowed(recentUser.id)}
                    isCurrentUser={recentUser.id === user?.uid}
                    showFollowButton={recentUser.id !== user?.uid}
                    onUserClick={handleUserProfileClick}
                    onFollowToggle={async (userId) => {
                      try {
                        const currentlyFollowing = isUserFollowed(userId);

                        // Optimistically update the recent users list
                        setRecentUsers(prev => prev.map(recentUser =>
                          recentUser.id === userId
                            ? { ...recentUser, data: { ...recentUser.data, isFollowing: !currentlyFollowing } }
                            : recentUser
                        ));

                        // Perform the follow/unfollow action
                        if (currentlyFollowing) {
                          await unfollowUser(userId);
                        } else {
                          await followUser(userId);
                        }
                      } catch (error) {
                        console.error('Error toggling follow status:', error);

                        // Revert optimistic update on error
                        const currentlyFollowing = isUserFollowed(userId);
                        setRecentUsers(prev => prev.map(recentUser =>
                          recentUser.id === userId
                            ? { ...recentUser, data: { ...recentUser.data, isFollowing: currentlyFollowing } }
                            : recentUser
                        ));
                      }
                    }}
                  />
                </Box>
              ))}
            </Masonry>
          ) : (
            <EmptyState message="No recent users" />
          )}
        </Box>
      );
    }

    if (activeSearchTab === TAB_PEOPLE) {
      return (
        <Box sx={{ mb: 4 }}>
          {heading}
          {loadingRecent ? (
            <Skeleton
              variant="rounded"
              width="100%"
              height={150}
              sx={{ borderRadius: 2, bgcolor: skBg }}
            />
          ) : recentPeople.length > 0 ? (
            <Masonry
              breakpointCols={{
                default: 2,
                900: 1,
                600: 1
              }}
              className="masonry-grid"
              columnClassName="masonry-grid_column"
            >
              {recentPeople.map((recentPerson) => {
                const isFavorite = favoritePeopleService.isFavorite(recentPerson.id, user?.uid);
                return (
                  <Box
                    key={recentPerson.id}
                    onClick={() =>
                      handlePersonClick({
                        id: recentPerson.id,
                        name: recentPerson.data.name,
                        profile_path: recentPerson.data.profile_path,
                        known_for_department: recentPerson.data.known_for_department,
                      })
                    }
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      px: 1.25,
                      py: 1.5,
                      borderRadius: 3,
                      bgcolor: theme.palette.background.paper,
                      cursor: 'pointer',
                      mb: 1.5,
                      '&:hover': {
                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'
                      }
                    }}
                  >
                    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                      <Avatar
                        src={recentPerson.data.profile_path
                          ? `https://image.tmdb.org/t/p/w200${recentPerson.data.profile_path}`
                          : undefined
                        }
                        sx={{ width: 48, height: 48 }}
                      >
                        {recentPerson.data.name.charAt(0).toUpperCase()}
                      </Avatar>
                      {isFavorite && (
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            right: 0,
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            bgcolor: '#ffffff',
                            border: '2px solid #222222',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <HeartIcon size={11} color="#FF1744" weight="fill" />
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {recentPerson.data.name}
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                        {recentPerson.data.known_for_department}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Masonry>
          ) : (
            <EmptyState message="No recent people" />
          )}
        </Box>
      );
    }

    return null;
  };

  // Pull-to-refresh handler - re-run current search
  const handleRefresh = useCallback(async () => {
    if (searchQuery.trim()) {
      await performSearch(searchQuery, activeSearchTab);
    }
  }, [searchQuery, activeSearchTab, performSearch]);

  return (
    <PullToRefresh onRefresh={handleRefresh}>
    <Box sx={{
      minHeight: '100vh',
      bgcolor: theme.palette.background.default,
      pb: { xs: 14, sm: 16, md: 12 }, // Increased padding to account for floating bottom navigation
      mb: { xs: 14, sm: 16, md: 12 },
      overflowX: 'hidden' // Prevent horizontal scrolling on the page
    }}>
      {/* Header with Back Button and Search - with safe area support for iOS PWA */}
      <Box sx={{
        position: 'fixed', left: 0, right: 0,
        top: 0,
        zIndex: 1000,
        bgcolor: isScrolled ? (theme.palette.mode === 'dark' ? 'rgba(28, 28, 30, 0.7)' : 'rgba(255, 255, 255, 0.6)') : 'transparent',
        backdropFilter: isScrolled ? 'blur(10px)' : 'none',
        borderBottom: `1px solid ${isScrolled ? (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)') : 'transparent'}`,
        transition: 'all 0.3s ease-in-out',
        // Include safe-area-inset-top for iOS PWA status bar
        pt: { xs: 'calc(var(--safe-area-inset-top) + 12px)', sm: 'calc(var(--safe-area-inset-top) + 16px)', md: 'calc(var(--safe-area-inset-top) + 24px)' },
        pb: { xs: 0.5, sm: 1, md: 1 },
      }}>
        <ResponsiveContainer>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0,
          }}>
            {/* Back Button — present in Explore too, but collapsed (width 0, opacity 0)
                so the transition into Active search slides it in smoothly. */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                overflow: 'hidden',
                width: searchActive ? 48 : 0,
                mr: searchActive ? 1.5 : 0,
                opacity: searchActive ? 1 : 0,
                transform: searchActive ? 'translateX(0)' : 'translateX(-8px)',
                pointerEvents: searchActive ? 'auto' : 'none',
                transition:
                  'width 0.28s cubic-bezier(0.4,0,0.2,1), margin-right 0.28s cubic-bezier(0.4,0,0.2,1), opacity 0.22s ease, transform 0.28s cubic-bezier(0.4,0,0.2,1)',
              }}
            >
              <IconButton
                onClick={handleBackPressed}
                tabIndex={searchActive ? 0 : -1}
                aria-hidden={!searchActive}
                sx={{
                  color: theme.palette.text.primary,
                  '&:hover': {
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'
                  }
                }}
              >
                <ArrowLeftIcon />
              </IconButton>
            </Box>

            {/* Search Input */}
            <Box sx={{ position: 'relative', flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, maxWidth: { xs: '100%', sm: 600, md: 600 } }}>
                <TextField
                  fullWidth
                  placeholder={placeholderText}
                  value={searchQuery}
                  onChange={handleSearchInputChange}
                  onFocus={handleSearchFocus}
                  onBlur={handleSearchBlur}
                  onKeyDown={handleKeyDown}
                  inputRef={inputRef}
                  inputProps={{
                    'data-testid': 'search-input',
                    autoComplete: 'on',
                    autoCorrect: 'on',
                    autoCapitalize: 'off',
                    spellCheck: true,
                    inputMode: 'search',
                    enterKeyHint: 'search'
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MagnifyingGlassIcon size={20} color={theme.palette.text.secondary} />
                      </InputAdornment>
                    ),
                    endAdornment: searchQuery && (
                      <InputAdornment position="end">
                        <IconButton onClick={clearSearch} size="small">
                          <Clear sx={{ fontSize: 20 }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: { xs: 3, sm: 3 },
                      bgcolor: theme.palette.background.paper,
                      '& fieldset': {
                        borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.1)',
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.mode === 'dark' ? '#8B88FF' : '#2B2982',
                      },
                    },
                    '& .MuiInputBase-input': {
                      py: { xs: 2, sm: 2 },
                      fontSize: { xs: 16, sm: 16 },
                      fontFamily: font(),
                    },
                  }}
                />

                {showVoiceSearch && (
                <IconButton
                  onClick={handleVoiceSearch}
                  disabled={false} // Always clickable to allow start/stop
                  title={isListening ? 'Click to stop listening' : 'Click to start voice search'}
                  sx={{
                    height: { xs: 52, sm: 58 },
                    width: { xs: 52, sm: 58 },
                    bgcolor: isListening
                      ? 'rgba(229, 57, 53, 0.9)' // Red when listening (stop button style)
                      : 'rgba(76, 175, 80, 0.15)', // Green when not listening (start button style)
                    color: isListening
                      ? 'rgba(255, 255, 255, 0.95)'
                      : 'rgba(76, 175, 80, 0.8)',
                    border: isListening
                      ? '2px solid rgba(244, 67, 54, 0.3)'
                      : '2px solid rgba(76, 175, 80, 0.3)',
                    animation: isListening ? 'pulse 2s infinite' : 'none',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: isListening
                        ? 'rgba(211, 47, 47, 0.9)'
                        : 'rgba(67, 160, 71, 0.2)',
                      transform: 'scale(1.05)',
                    },
                    '&:active': {
                      transform: 'scale(0.95)',
                    },
                    '@keyframes pulse': {
                      '0%': {
                        transform: 'scale(1)',
                        boxShadow: `0 0 0 0 rgba(229, 57, 53, 0.7)`
                      },
                      '70%': {
                        transform: 'scale(1.02)',
                        boxShadow: `0 0 0 10px rgba(229, 57, 53, 0)`
                      },
                      '100%': {
                        transform: 'scale(1)',
                        boxShadow: `0 0 0 0 rgba(229, 57, 53, 0)`
                      },
                    },
                  }}
                >
                  <Mic sx={{
                    fontSize: { xs: 20, sm: 22 },
                    animation: isListening ? 'bounce 1s infinite' : 'none',
                    '@keyframes bounce': {
                      '0%, 20%, 50%, 80%, 100%': {
                        transform: 'translateY(0)',
                      },
                      '40%': {
                        transform: 'translateY(-3px)',
                      },
                      '60%': {
                        transform: 'translateY(-1px)',
                      },
                    },
                  }} />
                </IconButton>
                )}
              </Box>
            </Box>
          </Box>
        </ResponsiveContainer>

        {/* Search Tabs — always visible on Search tab */}
        <ResponsiveContainer sx={{
          mt: { xs: 2, sm: 2.5, md: 3, lg: 3 },
          mb: 1,
          overflow: 'hidden',
          minWidth: 0,
        }}>
          <CustomTabs
            value={activeSearchTab}
            onChange={handleSearchTabChange}
            tabs={[
              { label: 'Movies', value: TAB_MOVIES, icon: <FilmSlateIcon /> },
              { label: 'Watchlists', value: TAB_WATCHLISTS, icon: <ListIcon /> },
              { label: 'Users', value: TAB_USERS, icon: <UserIcon /> },
              { label: 'Cast & Crew', value: TAB_PEOPLE, icon: <Star /> },
            ]}
            sx={{
              ...edgeToEdgeSx,
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: 1.5,
              }
            }}
          />
        </ResponsiveContainer>
        {/* Loading shimmer — absolutely positioned over the header's bottom border, zero layout impact */}
        {(searchLoading || searchUpdating) && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '3px',
              overflow: 'hidden',
              // Subtle track so the beam has something to travel over
              bgcolor: alpha(theme.palette.primary.main, 0.14),
              // Beam sweeps from left to right with a natural pause at each end
              '@keyframes searchShimmerBeam': {
                '0%':   { transform: 'translateX(-100%)' },
                '15%':  { transform: 'translateX(-100%)' },
                '85%':  { transform: 'translateX(167%)' },
                '100%': { transform: 'translateX(167%)' },
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '60%',
                height: '100%',
                // Tight gradient: transparent edges → primary colour → white-hot core
                background: `linear-gradient(
                  90deg,
                  transparent 0%,
                  ${alpha(theme.palette.primary.main, 0.25)} 18%,
                  ${alpha(theme.palette.primary.main, 0.80)} 42%,
                  rgba(255,255,255,0.96) 50%,
                  ${alpha(theme.palette.primary.main, 0.80)} 58%,
                  ${alpha(theme.palette.primary.main, 0.25)} 82%,
                  transparent 100%
                )`,
                animation: 'searchShimmerBeam 2.4s ease-in-out infinite',
              },
            }}
          />
        )}
      </Box>

      {/* Search Results Content — fixed offset; tabs always in header so height is constant */}
      <ResponsiveContainer sx={{
        pt: {
          xs: 'calc(var(--safe-area-inset-top) + 144px)',
          sm: 'calc(var(--safe-area-inset-top) + 160px)',
          md: 'calc(var(--safe-area-inset-top) + 192px)',
          lg: 'calc(var(--safe-area-inset-top) + 192px)',
        },
      }}>
        {/* Always show content when page loads or after search */}
        {(hasSearched || !searchQuery.trim()) && (
          <Box sx={{
            opacity: searchUpdating ? 0.65 : 1,
            transition: 'opacity 0.15s ease',
            pointerEvents: searchUpdating ? 'none' : 'auto',
          }}>
            {/* Search Loading */}
            {searchLoading && (
              <Box sx={{ py: 2 }}>
                {activeSearchTab === TAB_MOVIES && (
                  // Movie search loading
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
                    {Array.from({ length: 3 }).map((_, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          gap: 2,
                          p: 2,
                          borderRadius: 2,
                          bgcolor: theme.palette.mode === 'dark' ? '#2C2C2E' : '#F0EFF0',
                          minWidth: 0,
                        }}
                      >
                        <Skeleton
                          variant="rounded"
                          width={60}
                          height={90}
                          sx={{ borderRadius: 1, bgcolor: skBg, flexShrink: 0 }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Skeleton variant="text" width="80%" sx={{ bgcolor: skBg }} />
                          <Skeleton variant="text" width="60%" sx={{ bgcolor: skBg }} />
                          <Skeleton variant="text" width="40%" sx={{ bgcolor: skBg }} />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}

                {activeSearchTab === TAB_WATCHLISTS && (
                  // Watchlist search loading
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: { xs: 2, sm: 3 }
                  }}>
                    {Array.from({ length: 4 }).map((_, index) => (
                      <Box
                        key={index}
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          bgcolor: theme.palette.mode === 'dark' ? '#2C2C2E' : '#F0EFF0',
                          minWidth: 0,
                        }}
                      >
                        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                          {[0, 1, 2].map((k) => (
                            <Skeleton
                              key={k}
                              variant="rounded"
                              width={40}
                              height={60}
                              sx={{ borderRadius: 1, bgcolor: skBg }}
                            />
                          ))}
                        </Box>
                        <Skeleton variant="text" width="70%" sx={{ mb: 1, bgcolor: skBg }} />
                        <Skeleton variant="text" width="50%" sx={{ bgcolor: skBg }} />
                      </Box>
                    ))}
                  </Box>
                )}

                {activeSearchTab === TAB_USERS && (
                  // User search loading
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
                    {Array.from({ length: 3 }).map((_, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          p: 2,
                          borderRadius: 2,
                          bgcolor: theme.palette.mode === 'dark' ? '#2C2C2E' : '#F0EFF0',
                          minWidth: 0,
                        }}
                      >
                        <Skeleton
                          variant="circular"
                          width={48}
                          height={48}
                          sx={{ bgcolor: skBg, flexShrink: 0 }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                          <Skeleton variant="text" width="60%" sx={{ bgcolor: skBg }} />
                          <Skeleton variant="text" width="40%" sx={{ bgcolor: skBg }} />
                        </Box>
                        <Skeleton
                          variant="rounded"
                          width={80}
                          height={32}
                          sx={{ borderRadius: 2, bgcolor: skBg, flexShrink: 0 }}
                        />
                      </Box>
                    ))}
                  </Box>
                )}

                {activeSearchTab === TAB_PEOPLE && (
                  // Cast & Crew search loading
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
                    {Array.from({ length: 4 }).map((_, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          p: 2,
                          borderRadius: 2,
                          bgcolor: theme.palette.mode === 'dark' ? '#2C2C2E' : '#F0EFF0',
                          minWidth: 0,
                        }}
                      >
                        <Skeleton
                          variant="circular"
                          width={48}
                          height={48}
                          sx={{ bgcolor: skBg, flexShrink: 0 }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                          <Skeleton variant="text" width="70%" sx={{ bgcolor: skBg }} />
                          <Skeleton variant="text" width="50%" sx={{ bgcolor: skBg }} />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            )}

            {/* Search Results Content */}
            {!searchLoading && (
              <>
                {renderRecentSection()}

                {activeSearchTab === TAB_MOVIES && searchQuery.trim() && (
                  <InfiniteScrollContainer
                    hasMore={searchResults.pagination.movies.hasMore}
                    loading={loadingMore}
                    onLoadMore={handleLoadMore}
                    threshold={300}
                    loadingText="Loading more movies..."
                    endMessage="No more movies found"
                  >
                    <Masonry
                      breakpointCols={{
                        default: 2,
                        900: 1,
                        600: 1
                      }}
                      className="masonry-grid"
                      columnClassName="masonry-grid_column"
                    >
                      {searchResults.movies.map((movie) => {
                        const movieId = movie.id ? `tmdb_${movie.id}` : (movie.imdbID || 'unknown');
                        const matchInfo = searchResults.movieMatchInfo.get(movieId);
                        return (
                          <Box key={movieId} sx={{ mb: 1.5 }}>
                            <StandardMovieCard
                              movie={movie}
                              variant="list"
                              onClick={() => handleMovieClick(movieId || 'unknown')}
                              onToggleWatchlist={() => { }}
                              onToggleWatched={() => { }}
                              user={user}
                              matchedFields={matchInfo?.matchedFields}
                              relevanceScore={matchInfo?.relevanceScore}
                            />
                          </Box>
                        );
                      })}
                    </Masonry>
                    {searchResults.movies.length === 0 && (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <EmptyState message={`No movies found for "${searchQuery}"`} />
                      </Box>
                    )}
                  </InfiniteScrollContainer>
                )}

                {activeSearchTab === TAB_WATCHLISTS && searchQuery.trim() && (
                  searchResults.watchlists.length > 0 ? (
                    <InfiniteScrollContainer
                      hasMore={searchResults.pagination.watchlists.hasMore}
                      loading={loadingMore}
                      onLoadMore={handleLoadMore}
                      threshold={300}
                      loadingText="Loading more watchlists..."
                      endMessage="No more watchlists found"
                    >
                      <Masonry
                        breakpointCols={{
                          default: 2,
                          900: 1,
                          600: 1
                        }}
                        className="masonry-grid"
                        columnClassName="masonry-grid_column"
                      >
                        {searchResults.watchlists.map((watchlist) => {
                          const creatorInfo = getCreatorInfo(watchlist);
                          const moviePosters = watchlist.movieDetails?.slice(0, 3).map((movie: any) =>
                            movie.poster || movie.Poster || ''
                          ).filter(Boolean) || [];

                          return (
                            <Box key={watchlist.id} sx={{ mb: 1.5 }}>
                              <WatchlistWidget
                                watchlistType={watchlist.id}
                                user={user}
                                targetUserId={watchlist.createdBy}
                                isOtherUserWatchlist={watchlist.createdBy !== user?.uid}
                                moviePosters={moviePosters}
                                variant="compact"
                                onClick={() => handleWatchlistClickFromSearch(watchlist.id, watchlist)}
                                watchlistId={watchlist.id}
                                isOwned={false}
                                isFollowing={false}
                                showFollowButton={false}
                                onAuthorClick={undefined}
                                externalWatchlistData={{
                                  name: watchlist.name,
                                  description: watchlist.description,
                                  movieDetails: watchlist.movieDetails || [],
                                  isPrivate: watchlist.isPrivate || false,
                                  createdBy: watchlist.createdBy || user?.uid || '',
                                  createdByName: creatorInfo.name,
                                  createdByAvatar: creatorInfo.avatar,
                                  collaborators: watchlist.collaborators || [],
                                  collaboratorProfiles: (watchlist.collaborators || []).map((collabId: string) => ({
                                    uid: collabId,
                                    displayName: getUserDisplayName(collabId, '') || 'User',
                                    photoURL: getUserAvatar(collabId)
                                  })),
                                  followerCount: watchlist.followerCount || 0,
                                  updatedAt: watchlist.updatedAt,
                                  createdAt: watchlist.createdAt,
                                }}
                              />
                            </Box>
                          );
                        })}
                      </Masonry>
                    </InfiniteScrollContainer>
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        textAlign: 'center',
                        py: 1.5,
                        fontSize: 13
                      }}
                    >
                      No watchlists found for "{searchQuery}"
                    </Typography>
                  )
                )}

                {activeSearchTab === TAB_USERS && searchQuery.trim() && (
                  searchResults.profiles.length > 0 ? (
                    <InfiniteScrollContainer
                      hasMore={searchResults.pagination.profiles.hasMore}
                      loading={loadingMore}
                      onLoadMore={handleLoadMore}
                      threshold={300}
                      loadingText="Loading more users..."
                      endMessage="No more users found"
                    >
                      <Masonry
                        breakpointCols={{
                          default: 2,
                          900: 1,
                          600: 1
                        }}
                        className="masonry-grid"
                        columnClassName="masonry-grid_column"
                      >
                        {searchResults.profiles.map((profile) => (
                          <Box key={profile.uid} sx={{ mb: 1.5 }}>
                            <UserListingCard
                              id={profile.uid}
                              name={getUserDisplayName(profile.uid, profile.displayName || 'Anonymous User')}
                              username={getUserUsername(profile.uid, profile.username ? `@${profile.username}` : '@user')}
                              avatar={getUserAvatar(profile.uid) || profile.photoURL || ''}
                              stats={{
                                followers: profile.stats?.followersCount || 0,
                                watchlists: profile.stats?.listsCreated || 0,
                              }}
                              isFollowing={isUserFollowed(profile.uid)}
                              isCurrentUser={profile.uid === user?.uid}
                              showFollowButton={true}
                              onUserClick={(userId) => {
                                console.log('Navigate to user profile from search:', userId);
                                handleUserProfileClick(userId);
                              }}
                              onFollowToggle={async (userId) => {
                                try {
                                  const currentlyFollowing = isUserFollowed(userId);

                                  // Optimistically update the search results profiles tab
                                  setSearchResults(prev => ({
                                    ...prev,
                                    profiles: prev.profiles.map(profile =>
                                      profile.uid === userId
                                        ? { ...profile, isFollowing: !currentlyFollowing }
                                        : profile
                                    )
                                  }));

                                  // Perform the follow/unfollow action
                                  if (currentlyFollowing) {
                                    await unfollowUser(userId);
                                  } else {
                                    await followUser(userId);
                                  }
                                } catch (error) {
                                  console.error('Error toggling follow status:', error);

                                  // Revert optimistic update on error
                                  const currentlyFollowing = isUserFollowed(userId);
                                  setSearchResults(prev => ({
                                    ...prev,
                                    profiles: prev.profiles.map(profile =>
                                      profile.uid === userId
                                        ? { ...profile, isFollowing: currentlyFollowing }
                                        : profile
                                    )
                                  }));
                                }
                              }}
                            />
                          </Box>
                        ))}
                      </Masonry>
                    </InfiniteScrollContainer>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <EmptyState message={`No users found for "${searchQuery}"`} />
                    </Box>
                  )
                )}

                {activeSearchTab === TAB_PEOPLE && searchQuery.trim() && (
                  searchResults.people.length > 0 ? (
                    <InfiniteScrollContainer
                      hasMore={searchResults.pagination.people.hasMore}
                      loading={loadingMore}
                      onLoadMore={handleLoadMore}
                      threshold={300}
                      loadingText="Loading more people..."
                      endMessage="No more people found"
                    >
                      <Masonry
                        breakpointCols={{
                          default: 2,
                          900: 1,
                          600: 1
                        }}
                        className="masonry-grid"
                        columnClassName="masonry-grid_column"
                      >
                        {searchResults.people.map((person) => (
                          <Box
                            key={person.id}
                            onClick={() =>
                              handlePersonClick({
                                id: person.id,
                                name: person.name,
                                profile_path: person.profile_path,
                                known_for_department: person.known_for_department,
                              })
                            }
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                              px: 1.25,
                              py: 1.5,
                              borderRadius: 3,
                              bgcolor: theme.palette.background.paper,
                              cursor: 'pointer',
                              mb: 1.5,
                              '&:hover': {
                                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'
                              }
                            }}
                          >
                            <Avatar
                              src={person.profile_path
                                ? `https://image.tmdb.org/t/p/w200${person.profile_path}`
                                : undefined
                              }
                              sx={{
                                width: 48,
                                height: 48,
                              }}
                            >
                              {person.name.charAt(0).toUpperCase()}
                            </Avatar>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {person.name}
                              </Typography>
                              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                {person.known_for_department}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Masonry>
                    </InfiniteScrollContainer>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <EmptyState message={`No people found for "${searchQuery}"`} />
                    </Box>
                  )
                )}
              </>
            )}
          </Box>
        )}

      </ResponsiveContainer>
    </Box>
    </PullToRefresh>
  );
};

export default SearchPage;
